import { BehaviorSubject, Observable, of } from 'rxjs';
import {
  ActiveTableNameType,
  HurdlesAndSpreads,
  LegalProgrammes,
  ReferenceEntity,
  ReferenceObligation,
  StaticAuditData,
  StaticDataHasApproval,
  TableColTypes,
  TableCols,
  TableNames,
  UnderlyingAssetExpiryDate,
} from 'src/app/static-data/models/interfaces/static-data';
import {
  ActiveTableKeyType,
  FormattedIssuers,
  IssuerReferenceData,
  StaticDataArrayType,
  StaticDataEntryType,
  TargetMarkets,
  UnderlyingAssets,
  Underlyings,
} from '../models/interfaces/static-data';
import { FormattedEntities } from './../models/interfaces/static-data';

import { Injectable, Injector, ProviderToken } from '@angular/core';
import {
  AsyncValidatorFn,
  FormBuilder,
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { NgbDate } from '@ng-bootstrap/ng-bootstrap';
import { first, map, share, switchMap, take, tap } from 'rxjs/operators';
import { ApiService } from 'src/app/auth-o3/shared/services/api.service';
import { FeatureKeys } from 'src/app/feature-flag/feature-flag.model';
import { FeatureFlagService } from 'src/app/feature-flag/feature-flag.service';
import {
  Distributor,
  DistributorGroups,
  DistributorGroupsContact,
  DistributorIdentifier,
} from 'src/app/fees-management/distributor.const';
import { PhysicalSettlementMatrix } from 'src/app/form-config/mockups/consts/physical-settlement-matrix.const';
import { Contact } from 'src/app/product-capture/model/interfaces/contact.interface';
import { DropDownOption } from 'src/app/product-capture/model/interfaces/drop-down-option.interface';
import { IssuerEntity } from 'src/app/product-capture/model/interfaces/issuer-entity.interface';
import { CalendarType } from 'src/app/product-capture/model/interfaces/trading-calendar.interface';
import { NgbDateTimeStruct } from 'src/app/ui-kit/dc-date-input/date-input.consts';
import { DateUtils } from 'src/app/utils/date.utils';
import { environment } from 'src/environments/environment';
import { WorkflowManagementTaskDtos } from '../../product-capture/model/interfaces/workflows.interface';
import { WorkflowsService } from '../../product-capture/services/workflows.service';
import { TranslationFieldPipe } from '../../shared/pipes/translation-value.pipe';
import { AGENTS, Agent } from '../models/consts/agents.static-data';
import { BENEFIT_KEY } from '../models/consts/benefit-key.const';
import { CAPITAL_PROTECTION } from '../models/consts/capital-protection';
import { CONTACTS } from '../models/consts/contact';
import { CREDIT_RATING_DESCRIPTIONS } from '../models/consts/credit-rating-descriptions';
import { CREDIT_RATING_OUTLOOKS } from '../models/consts/credit-rating-outlooks';
import { CURRENCIES } from '../models/consts/currencies';
import {
  DATA_DICTIONARY_COLS,
  DataDictionary,
} from '../models/consts/data-dictionary';
import { DATA_EXPORT_FORMATS } from '../models/consts/data-export-formats.const';
import { DATA_INGESTION_CONFIG } from '../models/consts/data-ingestion-config.const';
import { DEFAULT_RISK_DESCRIPTIONS } from '../models/consts/default-risk-descriptions';
import { DESKS } from '../models/consts/desk';
import { DISTRIBUTOR_GROUPS } from '../models/consts/distributor-groups';
import { DISTRIBUTORS } from '../models/consts/distributors';
import { EXCHANGES } from '../models/consts/exchanges';
import { HURDLES_AND_SPREADS } from '../models/consts/hurdles-and-spreads';
import { ISSUERS } from '../models/consts/issuers';
import { ISSUERS_ENTITY } from '../models/consts/issuers-entity';
import { LANGUAGE } from '../models/consts/language';
import {
  LEGAL_ENTITY_COLS,
  LegalEntity,
} from '../models/consts/legal-entity.const';
import { LEGAL_PROGRAMMES } from '../models/consts/legal-programmes';
import { ListingVenueColumns as LISTING_VENUE_COLUMNS } from '../models/consts/listing-venue';
import { PAYOFFS } from '../models/consts/payoffs';
import { PHYSICAL_SETTLEMENT_MATRIX_COLUMNS } from '../models/consts/physical-settlement-matrix';
import { PRODUCT_SUB_TYPE } from '../models/consts/product-sub-type';
import { RECURRING_FEE } from '../models/consts/recurring-fee.const';
import { REFERENCE_ENTITY } from '../models/consts/reference-entity';
import { REFERENCE_OBLIGATION } from '../models/consts/reference-obligation';
import {
  APPROVALS_TEAMS_PER_STATIC_DATA_TYPE,
  STATIC_DATA_WORKFLOW_MAP,
} from '../models/consts/static-data-worflow-map';
import {
  STATIC_DATA_PERMISSIONS,
  STATIC_DATA_TYPE,
} from '../models/consts/static-data.enum';
import { TARGET_MARKETS } from '../models/consts/target-markets';
import { TRADING_CALENDARS } from '../models/consts/trading-calendars';
import { UKSPA_CODES } from '../models/consts/ukspa-codes';
import { UNDERLYING_ASSETS } from '../models/consts/underlying-assets';
import { UNDERLYINGS } from '../models/consts/underlyings';
import { SdBaseComponent } from '../sd-modal/components/sd-base.component';
import { IssuerIdentifier } from '../sd-modal/components/sd-issuers-entity/issuer-identifier.const';
import { FinancialCalendarService } from './financial-calendar-static-data.service';
import { STATIC_DATA_ENDPOINTS } from './static-data-endpoints.const';
import { StaticDataTableService } from './static-data-table.service';

const API_ROOT = environment.api.productCapture;
const HOSTNAME_STATIC_DATA_ENDPOINT = environment.api.staticData;
const HOSTNAME_STATIC_DATA_IPB = `${environment.api.ipb}staticdata/`;
@Injectable({
  providedIn: 'root',
})
export class StaticDataService {
  public showDeleted: boolean = false;
  public showSoftDeleted: boolean = false;

  private readonly translationFieldPipe = new TranslationFieldPipe();

  private readonly _activeTableKeySubject =
    new BehaviorSubject<ActiveTableKeyType>(null);

  public readonly activeTableKey$ = this._activeTableKeySubject.asObservable();

  public readonly activeTablePermissions$ = this.getTablePermissions();

  activeTableName: ActiveTableNameType;

  activeTableForm: UntypedFormGroup;

  _activeTableData: StaticDataArrayType = [];

  activeStaticDataComponent: SdBaseComponent;

  columnDisplayValuesLookup: { [K in ActiveTableKeyType]?: TableCols[] } = {
    payoffs: PAYOFFS,
    capitalProtection: CAPITAL_PROTECTION,
    ukspaCodes: UKSPA_CODES,
    underlyings: UNDERLYINGS,
    issuers: ISSUERS,
    creditRatingDescriptions: CREDIT_RATING_DESCRIPTIONS,
    creditRatingOutlooks: CREDIT_RATING_OUTLOOKS,
    defaultRiskDescriptions: DEFAULT_RISK_DESCRIPTIONS,
    tradingCalendars: TRADING_CALENDARS,
    settlementCalendars: TRADING_CALENDARS,
    targetMarkets: TARGET_MARKETS,
    referenceEntity: REFERENCE_ENTITY,
    referenceObligation: REFERENCE_OBLIGATION,
    agents: AGENTS,
    currency: CURRENCIES,
    issuersEntity: ISSUERS_ENTITY,
    legalProgrammes: LEGAL_PROGRAMMES,
    listingVenues: LISTING_VENUE_COLUMNS,
    language: LANGUAGE,
    physicalSettlementMatrix: PHYSICAL_SETTLEMENT_MATRIX_COLUMNS,
    underlyingAssets: UNDERLYING_ASSETS,
    exchanges: EXCHANGES,
    dataIngestionConfig: DATA_INGESTION_CONFIG,
    dataExportFormats: DATA_EXPORT_FORMATS,
    desks: DESKS,
    contacts: CONTACTS,
    hurdlesAndSpreads: HURDLES_AND_SPREADS,
    distributorGroups: DISTRIBUTOR_GROUPS,
    distributors: DISTRIBUTORS,
    productSubType: PRODUCT_SUB_TYPE,
    benefitKey: BENEFIT_KEY,
    legalEntity: LEGAL_ENTITY_COLS,
    dataDictionary: DATA_DICTIONARY_COLS,
    recurringFee: RECURRING_FEE,
  };

  columns: TableCols<TableColTypes>[];

  constructor(
    private apiService: ApiService,
    private router: Router,
    private fb: FormBuilder,
    private sdTableService: StaticDataTableService,
    private injector: Injector,
    private workflowService: WorkflowsService,
    private featureFlagService: FeatureFlagService
  ) { }

  set activeTableKey(value: ActiveTableKeyType) {
    this._activeTableKeySubject.next(value);
  }

  get activeTableKey(): ActiveTableKeyType {
    return this._activeTableKeySubject.getValue();
  }

  getTables(): Observable<any> {
    return this.sdTableService.getTables();
  }

  getSoftDeletedState() {
    this.featureFlagService.getFeatureFlag(FeatureKeys.SOFT_DELETE)
      .pipe(
        first(),
        tap(({ isEnabled: enabled }) => {
          // @ts-ignore
          this.showSoftDeleted = enabled === 1;
        })
      ).subscribe();
  }

  selectTable({ key, displayValue }: TableNames): void {
    this.activeTableKey = key;
    this.activeTableName = displayValue;

    // filters any null value from column defs
    // workaround for prod issue in settlement matrix
    this.columns = this.columnDisplayValuesLookup[key].filter((c) => !!c);

    // Feature Flag soft delete -> removes the deletedAt column from the form
    if (!this.showSoftDeleted) this.columns = this.columns.filter(c => c.key !== 'deletedAt');
    this.createForm();

    const route = this.formatRoute();
    this.router.navigate(['/static-data/', route]);
  }

  createForm(): void {
    const controls = {};

    this.columns
      ?.filter((val) => !val.hideOnForm)
      ?.forEach((name: TableCols) => {
        controls[name.key] =
          this.activeTableKey === 'legalProgrammes'
            ? [
                name.defaultValue !== undefined ? name.defaultValue : '',
                Validators.required,
              ]
            : [
                name.defaultValue !== undefined ? name.defaultValue : null,
                name.validators?.length ? name.validators : null,
              ];
      });

    this.activeTableForm = this.fb.group({ ...controls });

    if (
      this.activeTableKey === 'underlyingAssets' ||
      this.activeTableKey === 'issuersEntity'
    ) {
      this.addFormArray('identifiers');
    }

    if (this.activeTableKey === 'underlyingAssets') {
      this.addFormArray('contractExpiryDates');
    }

    if (this.activeTableKey === 'productSubType') {
      this.addFormArray('tenants');
      this.addFormArray('codes');
    }

    if (this.activeTableKey === 'legalProgrammes') {
      this.addFormArray('supplements');
    }
  }

  addFormArray(
    fieldName: string,
    validators: ValidatorFn[] = [],
    asyncValidators: AsyncValidatorFn[] = []
  ): void {
    this.activeTableForm.setControl(
      fieldName,
      new UntypedFormArray([], {
        validators: validators,
        asyncValidators: asyncValidators,
      })
    );
  }

  formatRoute(url = this.activeTableKey): string {
    const route = url.replace(/([a-z\xE0-\xFF])([A-Z\xC0\xDF])/g, '$1-$2');
    return route.toLowerCase();
  }

  // Dynamic form row adding speicifically for Underlying Assets > identifiers[]
  get getIdentifiers() {
    return this.activeTableForm.get('identifiers') as UntypedFormArray;
  }

  createIdentifiersRow(): UntypedFormGroup {
    return new UntypedFormGroup({
      type: new UntypedFormControl(null),
      code: new UntypedFormControl(null),
    });
  }

  addIdentifiersRow(): void {
    this.getIdentifiers.push(this.createIdentifiersRow());
  }

  get getSubTypeTenants(): UntypedFormArray {
    return this.activeTableForm.get('tenants') as UntypedFormArray;
  }

  createSubTypeTenantRow(): UntypedFormControl {
    return this.fb.control(null);
  }

  addSubTypeTenantRow(): void {
    this.getSubTypeTenants.push(this.createSubTypeTenantRow());
  }

  get getSubTypeCodes(): UntypedFormArray {
    return this.activeTableForm.get('codes') as UntypedFormArray;
  }

  createSubTypeCodesRow(): UntypedFormGroup {
    return this.fb.group({
      type: this.fb.control(null, { validators: [Validators.required] }),
      code: this.fb.control(null, { validators: [Validators.required] }),
    });
  }

  addLegalProgrammeSupplementsRow(): void {
    this.getLegalProgrammeSupplements.push(
      this.createLegalProgrammeSupplementsRow()
    );
  }

  get getLegalProgrammeSupplements(): UntypedFormArray {
    return this.activeTableForm.get('supplements') as UntypedFormArray;
  }

  createLegalProgrammeSupplementsRow(): UntypedFormGroup {
    return this.fb.group({
      supplementId: this.fb.control(null, { validators: Validators.required }),
      supplementDate: this.fb.control(null, {
        validators: Validators.required,
      }),
      type: this.fb.control(null, { validators: Validators.required }),
    });
  }

  addSubTypeCodesRow(): void {
    this.getSubTypeCodes.push(this.createSubTypeCodesRow());
  }

  get getExpiryDates(): UntypedFormArray {
    return this.activeTableForm.get('contractExpiryDates') as UntypedFormArray;
  }

  createExpiryDatesRow(): UntypedFormGroup {
    return this.fb.group({
      expiryDate: this.fb.control(null, { validators: [Validators.required] }),
      month: this.fb.control(null, { validators: [Validators.required] }),
      year: this.fb.control(null, { validators: [Validators.required] }),
      timezone: this.fb.control(null, { validators: [Validators.required] }),
      monthCode: this.fb.control(null),
      contractCode: this.fb.control(null),
      assetId: this.fb.control(null),
    });
  }

  addExpiryDatesRow(): void {
    this.getExpiryDates.push(this.createExpiryDatesRow());
  }

  // ** POST **

  addRecord(formValues: any): Observable<StaticDataEntryType> {
    const tableKey = this.activeTableKey;

    const newRecord = {
      ...formValues,
    };

    if (tableKey === STATIC_DATA_TYPE.RECURRING_FEE) {
      return this.apiService.post(
        STATIC_DATA_ENDPOINTS.RECURRING_FEE,
        newRecord,
        HOSTNAME_STATIC_DATA_IPB
      );
    }

    if (tableKey === STATIC_DATA_TYPE.BENEFIT_KEY) {
      return this.apiService.post(
        STATIC_DATA_ENDPOINTS.BENEFIT_KEY,
        newRecord,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.PRODUCT_SUB_TYPE) {
      return this.apiService.post(
        STATIC_DATA_ENDPOINTS.PRODUCT_SUB_TYPE,
        newRecord,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.PAYOFFS) {
      return this.apiService.post(
        STATIC_DATA_ENDPOINTS.PAYOFF,
        newRecord,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.CAPITAL_PROTECTION) {
      return this.apiService.post(
        STATIC_DATA_ENDPOINTS.CAPITAL_PROTECTION,
        newRecord,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.UKSPA_CODES) {
      return this.apiService.post(
        STATIC_DATA_ENDPOINTS.UKSPA_PRODUCT_CODES,
        newRecord,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.UNDERLYINGS) {
      const newUnderlyingsRecord = {
        ...formValues,
        referenceAsset: formValues.referenceAsset.id,
        underlyingAssetId: formValues.referenceAsset.underlyingAssetId,
      };

      return this.apiService.post(
        STATIC_DATA_ENDPOINTS.UNDERLYING_REFERENCE_DATA, // using old endpoint
        newUnderlyingsRecord,
        API_ROOT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.EXCHANGES) {
      return this.apiService.post(
        STATIC_DATA_ENDPOINTS.EXCHANGES,
        newRecord,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    // OK but missing 'website' property
    if (tableKey === STATIC_DATA_TYPE.ISSUERS) {
      const newIssuersRecord = {
        ...formValues,
        issuerId: formValues.longName.issuerId,
        longName: formValues.longName.id,
      };
      return this.apiService.post(
        STATIC_DATA_ENDPOINTS.ISSUERS,
        newIssuersRecord,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.CREDIT_RATING_DESCRIPTIONS) {
      return this.apiService.post(
        STATIC_DATA_ENDPOINTS.CREDIT_RATING,
        newRecord,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.CREDIT_RATING_OUTLOOKS) {
      return this.apiService.post(
        STATIC_DATA_ENDPOINTS.CREDIT_OUTLOOK,
        newRecord,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.LANGUAGE) {
      return this.apiService.post(
        STATIC_DATA_ENDPOINTS.LANGUAGE,
        newRecord,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.DEFAULT_RISK_DESCRIPTIONS) {
      return this.apiService.post(
        STATIC_DATA_ENDPOINTS.DEFAULT_RISK,
        newRecord,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (
      tableKey === STATIC_DATA_TYPE.TRADING_CALENDARS ||
      tableKey === STATIC_DATA_TYPE.SETTLEMENT_CALENDARS
    ) {
      return this.getService(FinancialCalendarService).add({
        ...formValues,
        type:
          tableKey === STATIC_DATA_TYPE.TRADING_CALENDARS
            ? CalendarType.TRADING
            : CalendarType.SETTLEMENT,
      });
    }

    if (tableKey === STATIC_DATA_TYPE.TARGET_MARKETS) {
      const { payoff: payoffId, ...rest } = newRecord;

      const newTargetMarket = {
        payoffId,
        ...rest,
      };

      return this.apiService
        .post(
          STATIC_DATA_ENDPOINTS.TARGET_MARKET,
          newTargetMarket,
          HOSTNAME_STATIC_DATA_ENDPOINT
        )
        .pipe<any>(take(1));
    }

    if (tableKey === STATIC_DATA_TYPE.REFERENCE_ENTITY) {
      return this.apiService.post(
        STATIC_DATA_ENDPOINTS.REFERENCE_ENTITY,
        newRecord,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.REFERENCE_OBLIGATION) {
      return this.apiService.post(
        STATIC_DATA_ENDPOINTS.REFERENCE_OBLIGATION,
        newRecord,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.ISSUERS_ENTITY) {
      const { identifiers, ...rest } = newRecord;
      const validIdentifiers = identifiers.filter(
        (item) => item?.type && item.code
      );

      return this.apiService.post(
        STATIC_DATA_ENDPOINTS.ISSUER_ENTITY,
        {
          ...rest,
          identifiers: validIdentifiers,
        },
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.LEGAL_PROGRAMMES) {
      const parsedSupplements = (
        (newRecord as LegalProgrammes).supplements ?? []
      ).map((supplement) => {
        return {
          ...supplement,
          supplementDate: DateUtils.getStringFromDateObject(
            supplement.supplementDate as unknown as NgbDate
          ),
        };
      });
      return this.apiService.post(
        STATIC_DATA_ENDPOINTS.LEGAL_PROGRAMMES,
        {
          ...newRecord,
          supplements: parsedSupplements,
        },
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.LISTING_VENUES) {
      return this.apiService.post(
        STATIC_DATA_ENDPOINTS.LISTING_VENUES,
        newRecord,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.AGENTS) {
      return this.apiService.post(
        STATIC_DATA_ENDPOINTS.AGENTS,
        newRecord,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.PHYSICAL_SETTLEMENT_MATRIX) {
      newRecord.id = newRecord.transactionType.toUpperCase();

      return this.apiService.post(
        STATIC_DATA_ENDPOINTS.PHYSICAL_SETTLEMENT_MATRIX,
        newRecord,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.UNDERLYING_ASSETS) {
      const { identifiers, contractExpiryDates, ...rest } = newRecord;
      const validIdentifiers = identifiers.filter(
        (item) => item?.type && item.code
      );
      const parsedContractExpiryDates = (
        (contractExpiryDates ?? []) as UnderlyingAssetExpiryDate[]
      ).map((expiryDate) => {
        return {
          ...expiryDate,
          expiryDate: DateUtils.getStringWithOffsetFromDateTimeObject(
            expiryDate.expiryDate as unknown as NgbDateTimeStruct,
            expiryDate.timezone
          ),
        };
      });

      return this.apiService.post(
        STATIC_DATA_ENDPOINTS.UNDERLYING_ASSETS,
        {
          ...rest,
          identifiers: validIdentifiers,
          contractExpiryDates: parsedContractExpiryDates,
        },
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.DATA_INGESTION_CONFIG) {
      return this.apiService.post(
        STATIC_DATA_ENDPOINTS.DATA_INGESTION_CONFIG,
        newRecord,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.DATA_EXPORT_FORMATS) {
      return this.apiService.post(
        STATIC_DATA_ENDPOINTS.DATA_EXPORT_FORMATS,
        newRecord,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.DESKS) {
      return this.apiService.post(
        STATIC_DATA_ENDPOINTS.DESKS,
        newRecord,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.CONTACTS) {
      return this.apiService.post(
        STATIC_DATA_ENDPOINTS.CONTACTS_PROFILE,
        newRecord,
        HOSTNAME_STATIC_DATA_IPB
      );
    }

    if (tableKey === STATIC_DATA_TYPE.CURRENCIES) {
      const transformedRecord = {
        ...newRecord,
        paymentDateOffset: newRecord.paymentDateOffset || null,
      };

      return this.apiService.post(
        STATIC_DATA_ENDPOINTS.CURRENCIES,
        transformedRecord,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.HURDLES_AND_SPREADS) {
      const recordDto = { ...newRecord };

      return this.apiService.post(
        STATIC_DATA_ENDPOINTS.HURDLES_AND_SPREADS,
        recordDto,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.DISTRIBUTORS) {
      return this.apiService.post(
        STATIC_DATA_ENDPOINTS.DISTRIBUTORS,
        newRecord,
        HOSTNAME_STATIC_DATA_IPB
      );
    }

    if (tableKey === STATIC_DATA_TYPE.DISTRIBUTOR_GROUPS) {
      return this.apiService.post(
        STATIC_DATA_ENDPOINTS.DISTRIBUTOR_GROUPS,
        newRecord,
        HOSTNAME_STATIC_DATA_IPB
      );
    }

    if (tableKey === STATIC_DATA_TYPE.LEGAL_ENTITY) {
      return this.apiService.post(
        STATIC_DATA_ENDPOINTS.LEGAL_ENTITY,
        newRecord,
        HOSTNAME_STATIC_DATA_IPB
      );
    }

    if (tableKey === STATIC_DATA_TYPE.DATA_DICTIONARY) {
      return this.apiService.post(
        STATIC_DATA_ENDPOINTS.DATA_DICTIONARY,
        newRecord,
        HOSTNAME_STATIC_DATA_IPB
      );
    }
  }

  // ** DELETE **
  deleteRecord(row: any): Observable<StaticDataEntryType> {
    const tableKey = this.activeTableKey;

    if (tableKey === STATIC_DATA_TYPE.RECURRING_FEE) {
      return this.apiService.deleteNoBody(
        `${STATIC_DATA_ENDPOINTS.RECURRING_FEE}/${row.id}`,
        HOSTNAME_STATIC_DATA_IPB
      );
    }

    if (tableKey === STATIC_DATA_TYPE.BENEFIT_KEY) {
      return this.apiService.deleteNoBody(
        `${STATIC_DATA_ENDPOINTS.BENEFIT_KEY}/${row.id}`,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.PRODUCT_SUB_TYPE) {
      return this.apiService.deleteNoBody(
        `${STATIC_DATA_ENDPOINTS.PRODUCT_SUB_TYPE}/${encodeURIComponent(
          row.subTypeName
        )}`,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.PAYOFFS) {
      return this.apiService.deleteNoBody(
        `${STATIC_DATA_ENDPOINTS.PAYOFF}/${row.id}`,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.CAPITAL_PROTECTION) {
      return this.apiService.deleteNoBody(
        `${STATIC_DATA_ENDPOINTS.CAPITAL_PROTECTION}/${row.id}`,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.UKSPA_CODES) {
      return this.apiService.deleteNoBody(
        `${STATIC_DATA_ENDPOINTS.UKSPA_PRODUCT_CODES}/${row.id}`,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.UNDERLYINGS) {
      return this.apiService.deleteNoBody(
        `${STATIC_DATA_ENDPOINTS.UNDERLYING_REFERENCE_DATA}/${row.underlyingAssetId}`,
        API_ROOT
      );
    }

    // ???? 500
    if (tableKey === STATIC_DATA_TYPE.ISSUERS) {
      return this.apiService.deleteNoBody(
        `${STATIC_DATA_ENDPOINTS.ISSUERS}/${row.issuerId}`,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.CREDIT_RATING_DESCRIPTIONS) {
      return this.apiService.deleteNoBody(
        `${STATIC_DATA_ENDPOINTS.CREDIT_RATING}/${row.id}`,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.CREDIT_RATING_OUTLOOKS) {
      return this.apiService.deleteNoBody(
        `${STATIC_DATA_ENDPOINTS.CREDIT_OUTLOOK}/${row.id}`,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.DEFAULT_RISK_DESCRIPTIONS) {
      return this.apiService.deleteNoBody(
        `${STATIC_DATA_ENDPOINTS.DEFAULT_RISK}/${row.id}`,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (
      tableKey === STATIC_DATA_TYPE.TRADING_CALENDARS ||
      tableKey === STATIC_DATA_TYPE.SETTLEMENT_CALENDARS
    ) {
      return this.getService(FinancialCalendarService).delete({
        ...row,
        type:
          tableKey === STATIC_DATA_TYPE.TRADING_CALENDARS
            ? CalendarType.TRADING
            : CalendarType.SETTLEMENT,
      });
    }

    if (tableKey === STATIC_DATA_TYPE.TARGET_MARKETS) {
      return this.apiService.deleteNoBody(
        `${STATIC_DATA_ENDPOINTS.TARGET_MARKET}/${row.id}`,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.LANGUAGE) {
      return this.apiService.deleteNoBody(
        `${STATIC_DATA_ENDPOINTS.LANGUAGE}/${row.id}`,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.REFERENCE_ENTITY) {
      return this.apiService.deleteNoBody(
        `${STATIC_DATA_ENDPOINTS.REFERENCE_ENTITY}/${row.referenceEntityID}`,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.REFERENCE_OBLIGATION) {
      return this.apiService.deleteNoBody(
        `${STATIC_DATA_ENDPOINTS.REFERENCE_OBLIGATION}/${row.referenceObligationID}`,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.ISSUERS_ENTITY) {
      return this.apiService.deleteNoBody(
        `${STATIC_DATA_ENDPOINTS.ISSUER_ENTITY}/${row.id}`,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.LEGAL_PROGRAMMES) {
      return this.apiService.deleteNoBody(
        `${STATIC_DATA_ENDPOINTS.LEGAL_PROGRAMMES}/${row.id}`,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.LISTING_VENUES) {
      return this.apiService.deleteNoBody(
        `${STATIC_DATA_ENDPOINTS.LISTING_VENUES}/${row.id}`,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.AGENTS) {
      return this.apiService.deleteNoBody(
        `${STATIC_DATA_ENDPOINTS.AGENTS}/${row.id}`,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.PHYSICAL_SETTLEMENT_MATRIX) {
      return this.apiService.deleteNoBody(
        `${STATIC_DATA_ENDPOINTS.PHYSICAL_SETTLEMENT_MATRIX}/${row.id}`,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.UNDERLYING_ASSETS) {
      return this.apiService.deleteNoBody(
        `${STATIC_DATA_ENDPOINTS.UNDERLYING_ASSETS}/${row.id}`,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.EXCHANGES) {
      return this.apiService.deleteNoBody(
        `${STATIC_DATA_ENDPOINTS.EXCHANGES}/${row.id}`,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.DATA_INGESTION_CONFIG) {
      return this.apiService.deleteNoBody(
        `${STATIC_DATA_ENDPOINTS.DATA_INGESTION_CONFIG}/${row.id}`,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.DATA_EXPORT_FORMATS) {
      return this.apiService.deleteNoBody(
        `${STATIC_DATA_ENDPOINTS.DATA_EXPORT_FORMATS}/${row.id}`,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.DESKS) {
      return this.apiService.deleteNoBody(
        `${STATIC_DATA_ENDPOINTS.DESKS}/${row.id}`,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.CONTACTS) {
      return this.apiService.deleteNoBody(
        `${STATIC_DATA_ENDPOINTS.CONTACTS_PROFILE}/${row.id}`,
        HOSTNAME_STATIC_DATA_IPB
      );
    }

    if (tableKey === STATIC_DATA_TYPE.HURDLES_AND_SPREADS) {
      return this.apiService.deleteNoBody(
        `${STATIC_DATA_ENDPOINTS.HURDLES_AND_SPREADS}/${row.id}`,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.LEGAL_ENTITY) {
      return this.apiService.deleteNoBody(
        `${STATIC_DATA_ENDPOINTS.LEGAL_ENTITY}/${row.id}`,
        HOSTNAME_STATIC_DATA_IPB
      );
    }

    if (tableKey === STATIC_DATA_TYPE.DISTRIBUTORS) {
      return this.apiService.deleteNoBody(
        `${STATIC_DATA_ENDPOINTS.DISTRIBUTORS}/${row.id}`,
        HOSTNAME_STATIC_DATA_IPB
      );
    }

    if (tableKey === STATIC_DATA_TYPE.DISTRIBUTOR_GROUPS) {
      return this.apiService.deleteNoBody(
        `${STATIC_DATA_ENDPOINTS.DISTRIBUTOR_GROUPS}/${row.id}`,
        HOSTNAME_STATIC_DATA_IPB
      );
    }
    if (tableKey === STATIC_DATA_TYPE.CURRENCIES) {
      return this.apiService.deleteNoBody(
        `${STATIC_DATA_ENDPOINTS.CURRENCIES}/${row.id}`,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.DATA_DICTIONARY) {
      return this.apiService.deleteNoBody(
        `${STATIC_DATA_ENDPOINTS.DATA_DICTIONARY}/${row.term}`,
        HOSTNAME_STATIC_DATA_IPB
      );
    }
  }

  // ** RESTORE **
  restoreRecord(row: any): Observable<StaticDataEntryType> {
    const tableKey = this.activeTableKey;

    if (tableKey === STATIC_DATA_TYPE.RECURRING_FEE) {
      return this.apiService.patch(
        `${STATIC_DATA_ENDPOINTS.RECURRING_FEE}/${row.id}/restore`,
        {},
        HOSTNAME_STATIC_DATA_IPB
      );
    }

    if (tableKey === STATIC_DATA_TYPE.BENEFIT_KEY) {
      return this.apiService.patch(
        `${STATIC_DATA_ENDPOINTS.BENEFIT_KEY}/${row.id}/restore`,
        {},
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.PRODUCT_SUB_TYPE) {
      return this.apiService.patch(
        `${STATIC_DATA_ENDPOINTS.PRODUCT_SUB_TYPE}/${encodeURIComponent(
          row.subTypeName
        )}/restore`,
        {},
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.PAYOFFS) {
      return this.apiService.patch(
        `${STATIC_DATA_ENDPOINTS.PAYOFF}/${row.id}/restore`,
        {},
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.LANGUAGE) {
      return this.apiService.patch(
        `${STATIC_DATA_ENDPOINTS.LANGUAGE}/${row.id}/restore`,
        {},
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.CURRENCIES) {
      return this.apiService.patch(
        `${STATIC_DATA_ENDPOINTS.CURRENCIES}/${row.id}/restore`,
        {},
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.CAPITAL_PROTECTION) {
      return this.apiService.patch(
        `${STATIC_DATA_ENDPOINTS.CAPITAL_PROTECTION}/${row.id}/restore`,
        {},
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.UKSPA_CODES) {
      return this.apiService.patch(
        `${STATIC_DATA_ENDPOINTS.UKSPA_PRODUCT_CODES}/${row.id}/restore`,
        {},
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.UNDERLYINGS) {
      return this.apiService.patch(
        `${STATIC_DATA_ENDPOINTS.UNDERLYING_REFERENCE_DATA}/${row.underlyingAssetId}/restore`,
        {},
        API_ROOT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.ISSUERS) {
      return this.apiService.patch(
        `${STATIC_DATA_ENDPOINTS.ISSUERS}/${row.issuerId}/restore`,
        {},
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.CREDIT_RATING_DESCRIPTIONS) {
      return this.apiService.patch(
        `${STATIC_DATA_ENDPOINTS.CREDIT_RATING}/${row.id}/restore`,
        {},
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.CREDIT_RATING_OUTLOOKS) {
      return this.apiService.patch(
        `${STATIC_DATA_ENDPOINTS.CREDIT_OUTLOOK}/${row.id}/restore`,
        {},
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.DEFAULT_RISK_DESCRIPTIONS) {
      return this.apiService.patch(
        `${STATIC_DATA_ENDPOINTS.DEFAULT_RISK}/${row.id}/restore`,
        {},
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (
      tableKey === STATIC_DATA_TYPE.TRADING_CALENDARS ||
      tableKey === STATIC_DATA_TYPE.SETTLEMENT_CALENDARS
    ) {
      return this.getService(FinancialCalendarService).restore({
        ...row,
        type:
          tableKey === STATIC_DATA_TYPE.TRADING_CALENDARS
            ? CalendarType.TRADING
            : CalendarType.SETTLEMENT,
      });
    }

    if (tableKey === STATIC_DATA_TYPE.TARGET_MARKETS) {
      return this.apiService.patch(
        `${STATIC_DATA_ENDPOINTS.TARGET_MARKET}/${row.id}/restore`,
        {},
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.REFERENCE_ENTITY) {
      return this.apiService.patch(
        `${STATIC_DATA_ENDPOINTS.REFERENCE_ENTITY}/${row.referenceEntityID}/restore`,
        {},
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.REFERENCE_OBLIGATION) {
      return this.apiService.patch(
        `${STATIC_DATA_ENDPOINTS.REFERENCE_OBLIGATION}/${row.referenceObligationID}/restore`,
        {},
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.ISSUERS_ENTITY) {
      return this.apiService.patch(
        `${STATIC_DATA_ENDPOINTS.ISSUER_ENTITY}/${row.id}/restore`,
        {},
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.LEGAL_PROGRAMMES) {
      return this.apiService.patch(
        `${STATIC_DATA_ENDPOINTS.LEGAL_PROGRAMMES}/${row.id}/restore`,
        {},
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.LISTING_VENUES) {
      return this.apiService.patch(
        `${STATIC_DATA_ENDPOINTS.LISTING_VENUES}/${row.id}/restore`,
        {},
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.AGENTS) {
      return this.apiService.patch(
        `${STATIC_DATA_ENDPOINTS.AGENTS}/${row.id}/restore`,
        {},
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.PHYSICAL_SETTLEMENT_MATRIX) {
      return this.apiService.patch(
        `${STATIC_DATA_ENDPOINTS.PHYSICAL_SETTLEMENT_MATRIX}/${row.id}/restore`,
        {},
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.UNDERLYING_ASSETS) {
      return this.apiService.patch(
        `${STATIC_DATA_ENDPOINTS.UNDERLYING_ASSETS}/${row.id}/restore`,
        {},
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.EXCHANGES) {
      return this.apiService.patch(
        `${STATIC_DATA_ENDPOINTS.EXCHANGES}/${row.id}/restore`,
        {},
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.DATA_INGESTION_CONFIG) {
      return this.apiService.patch(
        `${STATIC_DATA_ENDPOINTS.DATA_INGESTION_CONFIG}/${row.id}/restore`,
        {},
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.DATA_EXPORT_FORMATS) {
      return this.apiService.patch(
        `${STATIC_DATA_ENDPOINTS.DATA_EXPORT_FORMATS}/${row.id}/restore`,
        {},
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.DESKS) {
      return this.apiService.patch(
        `${STATIC_DATA_ENDPOINTS.DESKS}/${row.id}/restore`,
        {},
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.CONTACTS) {
      return this.apiService.patch(
        `${STATIC_DATA_ENDPOINTS.CONTACTS_PROFILE}/${row.id}/restore`,
        {},
        HOSTNAME_STATIC_DATA_IPB
      );
    }

    if (tableKey === STATIC_DATA_TYPE.HURDLES_AND_SPREADS) {
      return this.apiService.patch(
        `${STATIC_DATA_ENDPOINTS.HURDLES_AND_SPREADS}/${row.id}/restore`,
        {},
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.LEGAL_ENTITY) {
      return this.apiService
        .patch(
          `${STATIC_DATA_ENDPOINTS.LEGAL_ENTITY}/${row.id}/restore`,
          {},
          HOSTNAME_STATIC_DATA_IPB
        )
        .pipe(first<LegalEntity>());
    }

    if (tableKey === STATIC_DATA_TYPE.DISTRIBUTORS) {
      return this.apiService
        .patch(
          `${STATIC_DATA_ENDPOINTS.DISTRIBUTORS}/${row.id}/restore`,
          {},
          HOSTNAME_STATIC_DATA_IPB
        )
        .pipe(first<Distributor>());
    }

    if (tableKey === STATIC_DATA_TYPE.DISTRIBUTOR_GROUPS) {
      return this.apiService
        .patch(
          `${STATIC_DATA_ENDPOINTS.DISTRIBUTOR_GROUPS}/${row.id}/restore`,
          {},
          HOSTNAME_STATIC_DATA_IPB
        )
        .pipe(first<DistributorIdentifier>());
    }

    if (tableKey === STATIC_DATA_TYPE.DATA_DICTIONARY) {
      return this.apiService
        .patch(
          `${STATIC_DATA_ENDPOINTS.DATA_DICTIONARY}/${row.term}/restore`,
          {},
          HOSTNAME_STATIC_DATA_IPB
        )
        .pipe(first<any>());
    }
  }

  // ** PUT **
  editRecord(row, formValues): Observable<StaticDataEntryType> {
    const tableKey = this.activeTableKey;

    const editedRecord = {
      id: row.id,
      ...formValues,
    };

    if (tableKey === STATIC_DATA_TYPE.RECURRING_FEE) {
      const payload = { ...formValues };
      return this.apiService.put(
        `${STATIC_DATA_ENDPOINTS.RECURRING_FEE}/${row.id}`,
        payload,
        HOSTNAME_STATIC_DATA_IPB
      );
    }

    if (tableKey === STATIC_DATA_TYPE.BENEFIT_KEY) {
      return this.apiService.put(
        `${STATIC_DATA_ENDPOINTS.BENEFIT_KEY}/${row.id}`,
        editedRecord,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.PRODUCT_SUB_TYPE) {
      return this.apiService.put(
        `${STATIC_DATA_ENDPOINTS.PRODUCT_SUB_TYPE}/${encodeURIComponent(
          row.subTypeName
        )}`,
        editedRecord,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.PAYOFFS) {
      return this.apiService.put(
        `${STATIC_DATA_ENDPOINTS.PAYOFF}/${row.id}`,
        editedRecord,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.LANGUAGE) {
      return this.apiService.put(
        `${STATIC_DATA_ENDPOINTS.LANGUAGE}/${row.id}`,
        editedRecord,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.CURRENCIES) {
      const transformedRecord = {
        ...editedRecord,
        paymentDateOffset: editedRecord.paymentDateOffset || null,
      };

      return this.apiService.put(
        `${STATIC_DATA_ENDPOINTS.CURRENCIES}/${row.id}`,
        transformedRecord,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.CAPITAL_PROTECTION) {
      return this.apiService.put(
        `${STATIC_DATA_ENDPOINTS.CAPITAL_PROTECTION}/${row.id}`,
        editedRecord,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.UKSPA_CODES) {
      return this.apiService.put(
        `${STATIC_DATA_ENDPOINTS.UKSPA_PRODUCT_CODES}/${row.id}`,
        editedRecord,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.UNDERLYINGS) {
      const editedUnderlyings = {
        underlyingAssetId: row.underlyingAssetId,
        referenceAsset: row.referenceAsset,
        ...formValues,
      };
      return this.apiService.put(
        `${STATIC_DATA_ENDPOINTS.UNDERLYING_REFERENCE_DATA}/${row.underlyingAssetId}`,
        editedUnderlyings,
        API_ROOT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.ISSUERS) {
      const editedIssuerRecord = {
        issuerId: row.issuerId,
        ...formValues,
      };
      return this.apiService.put(
        `${STATIC_DATA_ENDPOINTS.ISSUERS}/${row.issuerId}`,
        editedIssuerRecord,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.CREDIT_RATING_DESCRIPTIONS) {
      return this.apiService.put(
        `${STATIC_DATA_ENDPOINTS.CREDIT_RATING}/${row.id}`,
        editedRecord,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.CREDIT_RATING_OUTLOOKS) {
      return this.apiService.put(
        `${STATIC_DATA_ENDPOINTS.CREDIT_OUTLOOK}/${row.id}`,
        editedRecord,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.DEFAULT_RISK_DESCRIPTIONS) {
      return this.apiService.put(
        `${STATIC_DATA_ENDPOINTS.DEFAULT_RISK}/${row.id}`,
        editedRecord,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (
      tableKey === STATIC_DATA_TYPE.TRADING_CALENDARS ||
      tableKey === STATIC_DATA_TYPE.SETTLEMENT_CALENDARS
    ) {
      return this.getService(FinancialCalendarService).edit(row, {
        ...editedRecord,
        type:
          tableKey === STATIC_DATA_TYPE.TRADING_CALENDARS
            ? CalendarType.TRADING
            : CalendarType.SETTLEMENT,
      });
    }

    if (tableKey === STATIC_DATA_TYPE.TARGET_MARKETS) {
      const { payoffId, id } = row;
      const { payoff, ...rest } = editedRecord;

      const newEditedRecord = {
        payoffId,
        ...rest,
      };

      return this.apiService.put(
        `${STATIC_DATA_ENDPOINTS.TARGET_MARKET}/${id}`,
        newEditedRecord,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.REFERENCE_ENTITY) {
      return this.apiService.put(
        `${STATIC_DATA_ENDPOINTS.REFERENCE_ENTITY}/${row.referenceEntityID}`,
        editedRecord,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.REFERENCE_OBLIGATION) {
      const { id, ...rest } = editedRecord;
      const newRecord = { ...rest };

      return this.apiService.put(
        `${STATIC_DATA_ENDPOINTS.REFERENCE_OBLIGATION}/${row.referenceObligationID}`,
        newRecord,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.ISSUERS_ENTITY) {
      let { identifiers, ...rest } = editedRecord;
      identifiers = identifiers.filter(
        (item: IssuerIdentifier) => item?.type && item.code
      );

      return this.apiService.put(
        `${STATIC_DATA_ENDPOINTS.ISSUER_ENTITY}/${row.id}`,
        {
          ...rest,
          ...(identifiers.length && { identifiers }),
        },
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.LEGAL_PROGRAMMES) {
      const parsedSupplements = (
        (editedRecord as LegalProgrammes).supplements ?? []
      ).map((supplement) => {
        return {
          ...supplement,
          supplementDate: DateUtils.getStringFromDateObject(
            supplement.supplementDate as unknown as NgbDate
          ),
        };
      });
      return this.apiService.put(
        `${STATIC_DATA_ENDPOINTS.LEGAL_PROGRAMMES}/${row.id}`,
        {
          ...editedRecord,
          supplements: parsedSupplements,
        },
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.LISTING_VENUES) {
      return this.apiService.put(
        `${STATIC_DATA_ENDPOINTS.LISTING_VENUES}/${row.id}`,
        editedRecord,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.AGENTS) {
      return this.apiService.put(
        `${STATIC_DATA_ENDPOINTS.AGENTS}/${row.id}`,
        editedRecord,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.PHYSICAL_SETTLEMENT_MATRIX) {
      return this.apiService.put(
        `${STATIC_DATA_ENDPOINTS.PHYSICAL_SETTLEMENT_MATRIX}/${row.id}`,
        editedRecord,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.UNDERLYING_ASSETS) {
      const { identifiers, contractExpiryDates, ...rest } = editedRecord;
      const validIdentifiers = identifiers.filter(
        (item) => item?.type && item.code
      );
      const parsedContractExpiryDates = (
        (contractExpiryDates ?? []) as UnderlyingAssetExpiryDate[]
      ).map((expiryDate) => {
        return {
          ...expiryDate,
          expiryDate: DateUtils.getStringWithOffsetFromDateTimeObject(
            expiryDate.expiryDate as unknown as NgbDateTimeStruct,
            expiryDate.timezone
          ),
        };
      });

      return this.apiService.put(
        `${STATIC_DATA_ENDPOINTS.UNDERLYING_ASSETS}/${row.id}`,
        {
          ...rest,
          identifiers: validIdentifiers,
          contractExpiryDates: parsedContractExpiryDates,
        },
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.EXCHANGES) {
      return this.apiService.put(
        `${STATIC_DATA_ENDPOINTS.EXCHANGES}/${row.id}`,
        editedRecord,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.DATA_INGESTION_CONFIG) {
      return this.apiService.put(
        `${STATIC_DATA_ENDPOINTS.DATA_INGESTION_CONFIG}/${row.id}`,
        editedRecord,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.DATA_EXPORT_FORMATS) {
      return this.apiService.put(
        `${STATIC_DATA_ENDPOINTS.DATA_EXPORT_FORMATS}/${row.id}`,
        editedRecord,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.DESKS) {
      return this.apiService.put(
        `${STATIC_DATA_ENDPOINTS.DESKS}/${row.id}`,
        editedRecord,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.CONTACTS) {
      return this.apiService.put(
        `${STATIC_DATA_ENDPOINTS.CONTACTS_PROFILE}/${row.id}`,
        editedRecord,
        HOSTNAME_STATIC_DATA_IPB
      );
    }

    if (tableKey === STATIC_DATA_TYPE.HURDLES_AND_SPREADS) {
      const recordDto = { ...editedRecord };

      return this.apiService.put(
        `${STATIC_DATA_ENDPOINTS.HURDLES_AND_SPREADS}/${row.id}`,
        recordDto,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (tableKey === STATIC_DATA_TYPE.LEGAL_ENTITY) {
      return this.apiService
        .put(
          `${STATIC_DATA_ENDPOINTS.LEGAL_ENTITY}/${row.id}`,
          editedRecord,
          HOSTNAME_STATIC_DATA_IPB
        )
        .pipe(first<LegalEntity>());
    }

    if (tableKey === STATIC_DATA_TYPE.DISTRIBUTORS) {
      return this.apiService
        .put(
          `${STATIC_DATA_ENDPOINTS.DISTRIBUTORS}/${row.id}`,
          editedRecord,
          HOSTNAME_STATIC_DATA_IPB
        )
        .pipe(first<Distributor>());
    }

    if (tableKey === STATIC_DATA_TYPE.DISTRIBUTOR_GROUPS) {
      return this.apiService
        .put(
          `${STATIC_DATA_ENDPOINTS.DISTRIBUTOR_GROUPS}/${row.id}`,
          editedRecord,
          HOSTNAME_STATIC_DATA_IPB
        )
        .pipe(first<DistributorIdentifier>());
    }

    if (tableKey === STATIC_DATA_TYPE.DATA_DICTIONARY) {
      return this.apiService
        .put(
          `${STATIC_DATA_ENDPOINTS.DATA_DICTIONARY}/${row.term}`,
          editedRecord,
          HOSTNAME_STATIC_DATA_IPB
        )
        .pipe(first<any>());
    }
  }

  // ** GET **
  getData(
    type?: ActiveTableKeyType,
    showDeleted?: boolean
  ): Observable<StaticDataArrayType> {
    if (!type) {
      type = this.activeTableKey;
    }

    if (type === STATIC_DATA_TYPE.RECURRING_FEE) {
      return this.apiService.get(
        STATIC_DATA_ENDPOINTS.RECURRING_FEE,
        HOSTNAME_STATIC_DATA_IPB
      );
    }

    if (type === STATIC_DATA_TYPE.BENEFIT_KEY) {
      return this.apiService.get(
        STATIC_DATA_ENDPOINTS.BENEFIT_KEY,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (type === STATIC_DATA_TYPE.PRODUCT_SUB_TYPE) {
      return this.apiService.get(
        STATIC_DATA_ENDPOINTS.PRODUCT_SUB_TYPE,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (type === STATIC_DATA_TYPE.PAYOFFS) {
      return this.apiService.get(
        STATIC_DATA_ENDPOINTS.PAYOFFS,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (type === STATIC_DATA_TYPE.CAPITAL_PROTECTION) {
      return this.apiService.get(
        STATIC_DATA_ENDPOINTS.CAPITAL_PROTECTIONS,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (type === STATIC_DATA_TYPE.UKSPA_CODES) {
      return this.apiService.get(
        STATIC_DATA_ENDPOINTS.UKSPA_PRODUCT_CODES,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (type === STATIC_DATA_TYPE.UNDERLYINGS) {
      return this.apiService.get(
        STATIC_DATA_ENDPOINTS.UNDERLYING_REFERENCE_DATA,
        API_ROOT
      );
    }

    if (type === STATIC_DATA_TYPE.ISSUERS) {
      return this.apiService.get(
        STATIC_DATA_ENDPOINTS.ISSUERS,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (type === STATIC_DATA_TYPE.LANGUAGE) {
      return this.apiService.get(
        STATIC_DATA_ENDPOINTS.LANGUAGE,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (type === STATIC_DATA_TYPE.CREDIT_RATING_DESCRIPTIONS) {
      return this.apiService.get(
        STATIC_DATA_ENDPOINTS.CREDIT_RATINGS,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (type === STATIC_DATA_TYPE.CREDIT_RATING_OUTLOOKS) {
      return this.apiService.get(
        STATIC_DATA_ENDPOINTS.CREDIT_OUTLOOKS,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (type === STATIC_DATA_TYPE.DEFAULT_RISK_DESCRIPTIONS) {
      return this.apiService.get(
        STATIC_DATA_ENDPOINTS.DEFAULT_RISKS,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (type === STATIC_DATA_TYPE.TRADING_CALENDARS) {
      return this.getService(FinancialCalendarService).getAllOfType(
        CalendarType.TRADING
      );
    }

    if (type === STATIC_DATA_TYPE.SETTLEMENT_CALENDARS) {
      return this.getService(FinancialCalendarService).getAllOfType(
        CalendarType.SETTLEMENT
      );
    }

    if (type === STATIC_DATA_TYPE.TARGET_MARKETS) {
      return this.apiService.get(
        STATIC_DATA_ENDPOINTS.TARGET_MARKET,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (type === STATIC_DATA_TYPE.REFERENCE_ENTITY) {
      return this.apiService
        .get(
          `${STATIC_DATA_ENDPOINTS.REFERENCE_ENTITY}?withDeleted=${showDeleted}`,
          HOSTNAME_STATIC_DATA_ENDPOINT
        )
        .pipe(
          map((data: ReferenceEntity[]) =>
            data.sort((a: ReferenceEntity, b: ReferenceEntity) =>
              this.translationFieldPipe
                .transform(a.longName)
                .localeCompare(this.translationFieldPipe.transform(b.longName))
            )
          ),
          map((data: ReferenceEntity[]) => {
            return data.map((item: ReferenceEntity & StaticDataHasApproval) => {
              return {
                ...item,
                approvals: item.approvals ?? [],
              };
            });
          }),
          share<ReferenceEntity[]>()
        );
    }

    if (type === STATIC_DATA_TYPE.REFERENCE_OBLIGATION) {
      return this.apiService
        .get(
          `${STATIC_DATA_ENDPOINTS.REFERENCE_OBLIGATION}?withDeleted=${showDeleted}`,
          HOSTNAME_STATIC_DATA_ENDPOINT
        )
        .pipe(take<ReferenceObligation[]>(1));
    }

    if (type === STATIC_DATA_TYPE.ISSUERS_ENTITY) {
      return this.apiService.get(
        `${STATIC_DATA_ENDPOINTS.ISSUERS_ENTITY}?withDeleted=${showDeleted}`,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (type === STATIC_DATA_TYPE.LEGAL_PROGRAMMES) {
      return this.apiService
        .get(
          STATIC_DATA_ENDPOINTS.LEGAL_PROGRAMMES,
          HOSTNAME_STATIC_DATA_ENDPOINT
        )
        .pipe(
          map((data: LegalProgrammes[]) => {
            return data.map((item) => {
              item.programmeDate = DateUtils.convertStringToNgbDate(
                item.programmeDate as string,
                'YYYY-MM-DD'
              );
              item.documentRegistrationDate = DateUtils.convertStringToNgbDate(
                item.documentRegistrationDate as string,
                'YYYY-MM-DD'
              );
              item.authorityApprovalDate = DateUtils.convertStringToNgbDate(
                item.authorityApprovalDate as string,
                'YYYY-MM-DD'
              );
              item.validUntilDate = DateUtils.convertStringToNgbDate(
                item.validUntilDate as string,
                'YYYY-MM-DD'
              );
              item.documentApprovalDate = DateUtils.convertStringToNgbDate(
                item.documentApprovalDate as string,
                'YYYY-MM-DD'
              );
              item.supplements = (item.supplements ?? []).map((supplement) => {
                return {
                  ...supplement,
                  supplementDate: DateUtils.convertStringToNgbDate(
                    supplement.supplementDate as string,
                    'YYYY-MM-DD'
                  ) as unknown as string,
                };
              });
              return item;
            });
          })
        );
    }

    if (type === STATIC_DATA_TYPE.LISTING_VENUES) {
      return this.apiService.get(
        `${STATIC_DATA_ENDPOINTS.LISTING_VENUES}?withDeleted=${showDeleted}`,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (type === STATIC_DATA_TYPE.AGENTS) {
      return this.apiService
        .get(
          `${STATIC_DATA_ENDPOINTS.AGENTS}s?withDeleted=${showDeleted}`,
          HOSTNAME_STATIC_DATA_ENDPOINT
        )
        .pipe(take<Agent[]>(1));
    }

    if (type === STATIC_DATA_TYPE.CURRENCIES) {
      return this.apiService
        .get(STATIC_DATA_ENDPOINTS.CURRENCIES, HOSTNAME_STATIC_DATA_ENDPOINT)
        .pipe(
          map((response: any[]) =>
            response.map((currency) => ({
              ...currency,
              isEmergingMarket: Boolean(currency.isEmergingMarket),
            }))
          )
        );
    }

    if (type === STATIC_DATA_TYPE.PHYSICAL_SETTLEMENT_MATRIX) {
      return this.apiService.get(
        STATIC_DATA_ENDPOINTS.PHYSICAL_SETTLEMENT_MATRIX,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (type === STATIC_DATA_TYPE.UNDERLYING_ASSETS) {
      return this.apiService
        .get(
          `${STATIC_DATA_ENDPOINTS.UNDERLYING_ASSETS}?withDeleted=${showDeleted}`,
          HOSTNAME_STATIC_DATA_ENDPOINT
        )
        .pipe(
          map((data: UnderlyingAssets[]) => {
            return data.map((item) => {
              item.contractExpiryDates = (item.contractExpiryDates ?? []).map(
                (expiryDate) => {
                  return {
                    ...expiryDate,
                    expiryDate: DateUtils.convertDateTimeStringToNgbDateTime(
                      expiryDate.expiryDate as string
                    ),
                  };
                }
              );
              return item;
            });
          })
        );
    }

    if (type === STATIC_DATA_TYPE.EXCHANGES) {
      return this.apiService.get(
        STATIC_DATA_ENDPOINTS.EXCHANGES,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (type === STATIC_DATA_TYPE.DATA_INGESTION_CONFIG) {
      return this.apiService.get(
        STATIC_DATA_ENDPOINTS.DATA_INGESTION_CONFIG,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (type === STATIC_DATA_TYPE.DATA_EXPORT_FORMATS) {
      return this.apiService.get(
        STATIC_DATA_ENDPOINTS.DATA_EXPORT_FORMATS,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (type === STATIC_DATA_TYPE.DESKS) {
      return this.apiService.get(
        STATIC_DATA_ENDPOINTS.DESKS,
        HOSTNAME_STATIC_DATA_ENDPOINT
      );
    }

    if (type === STATIC_DATA_TYPE.CONTACTS) {
      return this.apiService.get(
        STATIC_DATA_ENDPOINTS.CONTACTS_PROFILE,
        HOSTNAME_STATIC_DATA_IPB
      );
    }

    if (type === STATIC_DATA_TYPE.HURDLES_AND_SPREADS) {
      return this.apiService
        .get(
          STATIC_DATA_ENDPOINTS.HURDLES_AND_SPREADS,
          HOSTNAME_STATIC_DATA_ENDPOINT
        )
        .pipe(
          map((data: HurdlesAndSpreads[]) => {
            return data.map((item) => {
              item.asOfDate = DateUtils.convertStringToNgbDate(
                item.asOfDate,
                'DD-MM-YYYY'
              );
              return item;
            });
          })
        );
    }

    if (type === STATIC_DATA_TYPE.DISTRIBUTOR_GROUPS) {
      return this.apiService
        .get<DistributorGroups[]>(
          `${STATIC_DATA_ENDPOINTS.DISTRIBUTOR_GROUPS}?withDeleted=${showDeleted}`,
          HOSTNAME_STATIC_DATA_IPB
        )
        .pipe(first());
    }

    if (type === STATIC_DATA_TYPE.DISTRIBUTORS) {
      return this.apiService
        .get<Distributor[]>(
          `${STATIC_DATA_ENDPOINTS.DISTRIBUTORS}?withDeleted=${showDeleted}`,
          HOSTNAME_STATIC_DATA_IPB
        )
        .pipe(first());
    }

    if (type === STATIC_DATA_TYPE.LEGAL_ENTITY) {
      return this.apiService
        .get<LegalEntity[]>(
          STATIC_DATA_ENDPOINTS.LEGAL_ENTITY,
          HOSTNAME_STATIC_DATA_IPB
        )
        .pipe(first());
    }

    if (type === STATIC_DATA_TYPE.DATA_DICTIONARY) {
      return this.apiService
        .get<DataDictionary[]>(
          STATIC_DATA_ENDPOINTS.DATA_DICTIONARY,
          HOSTNAME_STATIC_DATA_IPB
        )
        .pipe(first());
    }
  }

  filterUnderlyingAssetsTableData(
    underlyingAssets: UnderlyingAssets[],
    underlyingReferenceData: Underlyings[]
  ): Array<UnderlyingAssets> {
    return underlyingAssets.filter((item) => {
      return !underlyingReferenceData.some(
        (entry) => entry.underlyingAssetId === item.id
      );
    });
  }

  getIssuersTable(): Observable<Array<IssuerEntity>> {
    return this.apiService.get(
      STATIC_DATA_TYPE.ISSUERS,
      HOSTNAME_STATIC_DATA_ENDPOINT
    );
  }

  getReferenceEntityDataById(id: number): Observable<ReferenceEntity> {
    return this.apiService.get(
      `${STATIC_DATA_ENDPOINTS.REFERENCE_ENTITY}/${id}`,
      HOSTNAME_STATIC_DATA_ENDPOINT
    );
  }

  getDistributorGroupContacts(
    id: number,
    role = ''
  ): Observable<DropDownOption[]> {
    return this.apiService
      .getWithParams(
        `${STATIC_DATA_ENDPOINTS.DISTRIBUTOR_GROUPS}/${id}/contacts`,
        { role },
        HOSTNAME_STATIC_DATA_IPB
      )
      .pipe(
        first<DistributorGroupsContact[]>(),
        map((contacts) =>
          contacts
            .map((contact) => ({
              id: contact.id,
              displayValue: `${contact.contactDetails.firstName} ${contact.contactDetails.lastName}`,
            }))
            .sort((a, b) => a.displayValue.localeCompare(b.displayValue))
        )
      );
  }

  getContactsByDeskType(deskType: string): Observable<Contact[]> {
    return this.apiService
      .get(
        `${STATIC_DATA_ENDPOINTS.CONTACTS_PROFILE}/desks/${deskType}`,
        HOSTNAME_STATIC_DATA_IPB
      )
      .pipe(first<Contact[]>());
  }

  filterIssuersTableData(
    issuers: IssuerEntity[],
    issuerReferenceData: IssuerReferenceData[]
  ): Array<IssuerEntity> {
    return issuers.filter((item) => {
      return !issuerReferenceData.some((entry) => entry.issuerId === item.id);
    });
  }

  formatIssuersTableDataItems(
    filteredIssuers: IssuerEntity[]
  ): FormattedIssuers[] {
    return filteredIssuers.map((item) => {
      return {
        id: { id: item.entityName, issuerId: item.id },
        displayValue: item.entityName,
      };
    });
  }

  formatRefEntityTableItems(
    refEntityList: ReferenceEntity[]
  ): FormattedEntities[] {
    return refEntityList.map((item) => {
      return {
        id: item.referenceEntityID,
        displayValue: this.translationFieldPipe.transform(item.longName),
      };
    });
  }

  formatIssuerEntityTableItems(issuerEntityList: any): any {
    return issuerEntityList.map((item) => {
      return {
        id: item.id,
        displayValue: item.entityName,
      };
    });
  }

  formatUnderlyingAssetTableItems(underlyingAssetsList: any): any {
    return underlyingAssetsList.map((item) => {
      return {
        id: item.id,
        displayValue: item.name,
      };
    });
  }

  formatExchangeItems(exchanges: any): any {
    return exchanges.map((item) => {
      return {
        id: item.id,
        displayValue: item.micCode
          ? `${item.micCode} | ${item.longName}`
          : item.longName,
      };
    });
  }

  formatDeskItems(desks: any): any {
    return desks.map((item) => {
      return {
        id: item.id,
        displayValue: item.name,
      };
    });
  }

  formatTradingCalendarsTableItems(tradingCalendars: any): any {
    const uniqueCalendarCodes = [
      ...new Set(tradingCalendars.map((item) => item.calendarCode)),
    ];

    return uniqueCalendarCodes.map((item) => {
      return {
        id: item,
        displayValue: item,
      };
    });
  }

  getPhysicalMatrixDataByTransactionType(
    transactionType: string
  ): Observable<PhysicalSettlementMatrix> {
    return this.apiService
      .get<PhysicalSettlementMatrix[]>(
        `${
          STATIC_DATA_ENDPOINTS.PHYSICAL_SETTLEMENT_MATRIX
        }?transactionType=${encodeURIComponent(transactionType)}`,
        HOSTNAME_STATIC_DATA_ENDPOINT
      )
      .pipe(
        switchMap((data: PhysicalSettlementMatrix[]) => of(data[0])),
        take<PhysicalSettlementMatrix>(1)
      );
  }

  getStaticDataApprovalTasks(): Observable<WorkflowManagementTaskDtos[]> {
    const context: string = STATIC_DATA_WORKFLOW_MAP[this.activeTableKey];
    if (context) {
      return this.workflowService.getUserApprovalTasks(context);
    }
    return of([]);
  }

  getAllAuditData<T = any>(
    staticDataId: string | number
  ): Observable<StaticAuditData<T>[]> {
    let staticDataEndpoint = '';
    let baseUrl = HOSTNAME_STATIC_DATA_ENDPOINT;
    const type: string = this.activeTableKey;

    switch (type) {
      case STATIC_DATA_TYPE.BENEFIT_KEY:
        staticDataEndpoint = STATIC_DATA_ENDPOINTS.BENEFIT_KEY;
        break;
      case STATIC_DATA_TYPE.PRODUCT_SUB_TYPE:
        staticDataEndpoint = STATIC_DATA_ENDPOINTS.PRODUCT_SUB_TYPE;
        break;
      case STATIC_DATA_TYPE.PAYOFFS:
        staticDataEndpoint = STATIC_DATA_ENDPOINTS.PAYOFFS;
        break;
      case STATIC_DATA_TYPE.CAPITAL_PROTECTION:
        staticDataEndpoint = STATIC_DATA_ENDPOINTS.CAPITAL_PROTECTION;
        break;
      case STATIC_DATA_TYPE.UKSPA_CODES:
        staticDataEndpoint = STATIC_DATA_ENDPOINTS.UKSPA_PRODUCT_CODES;
        break;
      case STATIC_DATA_TYPE.UNDERLYINGS:
        staticDataEndpoint = STATIC_DATA_ENDPOINTS.UNDERLYING_REFERENCE_DATA;
        break;
      case STATIC_DATA_TYPE.ISSUERS:
        staticDataEndpoint = STATIC_DATA_ENDPOINTS.ISSUERS;
        break;
      case STATIC_DATA_TYPE.LANGUAGE:
        staticDataEndpoint = STATIC_DATA_ENDPOINTS.LANGUAGE;
        break;
      case STATIC_DATA_TYPE.CREDIT_RATING_DESCRIPTIONS:
        staticDataEndpoint = STATIC_DATA_ENDPOINTS.CREDIT_RATINGS;
        break;
      case STATIC_DATA_TYPE.CREDIT_RATING_OUTLOOKS:
        staticDataEndpoint = STATIC_DATA_ENDPOINTS.CREDIT_OUTLOOKS;
        break;
      case STATIC_DATA_TYPE.DEFAULT_RISK_DESCRIPTIONS:
        staticDataEndpoint = STATIC_DATA_ENDPOINTS.DEFAULT_RISKS;
        break;
      case STATIC_DATA_TYPE.TRADING_CALENDARS:
        staticDataEndpoint = STATIC_DATA_ENDPOINTS.TRADING_CALENDARS;
        break;
      case STATIC_DATA_TYPE.SETTLEMENT_CALENDARS:
        staticDataEndpoint = STATIC_DATA_ENDPOINTS.TRADING_CALENDARS;
        break;
      case STATIC_DATA_TYPE.TARGET_MARKETS:
        staticDataEndpoint = STATIC_DATA_ENDPOINTS.TARGET_MARKET;
        break;
      case STATIC_DATA_TYPE.REFERENCE_ENTITY:
        staticDataEndpoint = STATIC_DATA_ENDPOINTS.REFERENCE_ENTITY;
        break;
      case STATIC_DATA_TYPE.REFERENCE_OBLIGATION:
        staticDataEndpoint = STATIC_DATA_ENDPOINTS.REFERENCE_OBLIGATION;
        break;
      case STATIC_DATA_TYPE.ISSUERS_ENTITY:
        staticDataEndpoint = STATIC_DATA_ENDPOINTS.ISSUER_ENTITY;
        break;
      case STATIC_DATA_TYPE.LEGAL_PROGRAMMES:
        staticDataEndpoint = STATIC_DATA_ENDPOINTS.LEGAL_PROGRAMMES;
        break;
      case STATIC_DATA_TYPE.LISTING_VENUES:
        staticDataEndpoint = STATIC_DATA_ENDPOINTS.LISTING_VENUES;
        break;
      case STATIC_DATA_TYPE.AGENTS:
        staticDataEndpoint = STATIC_DATA_ENDPOINTS.AGENTS;
        break;
      case STATIC_DATA_TYPE.CURRENCIES:
        staticDataEndpoint = STATIC_DATA_ENDPOINTS.CURRENCIES;
        break;
      case STATIC_DATA_TYPE.PHYSICAL_SETTLEMENT_MATRIX:
        staticDataEndpoint = STATIC_DATA_ENDPOINTS.PHYSICAL_SETTLEMENT_MATRIX;
        break;
      case STATIC_DATA_TYPE.UNDERLYING_ASSETS:
        staticDataEndpoint = STATIC_DATA_ENDPOINTS.UNDERLYING_ASSETS;
        break;
      case STATIC_DATA_TYPE.EXCHANGES:
        staticDataEndpoint = STATIC_DATA_ENDPOINTS.EXCHANGES;
        break;
      case STATIC_DATA_TYPE.DATA_INGESTION_CONFIG:
        staticDataEndpoint = STATIC_DATA_ENDPOINTS.DATA_INGESTION_CONFIG;
        break;
      case STATIC_DATA_TYPE.DATA_EXPORT_FORMATS:
        staticDataEndpoint = STATIC_DATA_ENDPOINTS.DATA_EXPORT_FORMATS;
        break;
      case STATIC_DATA_TYPE.DESKS:
        staticDataEndpoint = STATIC_DATA_ENDPOINTS.DESKS;
        break;
      case STATIC_DATA_TYPE.CONTACTS:
        staticDataEndpoint = STATIC_DATA_ENDPOINTS.CONTACTS;
        break;
      case STATIC_DATA_TYPE.HURDLES_AND_SPREADS:
        staticDataEndpoint = STATIC_DATA_ENDPOINTS.HURDLES_AND_SPREADS;
        break;
      case STATIC_DATA_TYPE.DISTRIBUTOR_GROUPS:
        staticDataEndpoint = STATIC_DATA_ENDPOINTS.DISTRIBUTOR_GROUPS_AUDIT;
        baseUrl = HOSTNAME_STATIC_DATA_IPB;
        break;
      case STATIC_DATA_TYPE.DISTRIBUTORS:
        staticDataEndpoint = STATIC_DATA_ENDPOINTS.DISTRIBUTOR_DETAILS_AUDIT;
        baseUrl = HOSTNAME_STATIC_DATA_IPB;
        break;
      case STATIC_DATA_TYPE.LEGAL_ENTITY:
        staticDataEndpoint = STATIC_DATA_ENDPOINTS.LEGAL_ENTITY;
        break;
    }

    return this.apiService
      .get<StaticAuditData<T>[]>(
        `${staticDataEndpoint}/${staticDataId}${
          staticDataEndpoint.endsWith('-audit') ? '' : '/versions'
        }`,
        baseUrl
      )
      .pipe(
        map((data: StaticAuditData<T>[]) => {
          return data.map((item: StaticAuditData<T>) => {
            return {
              ...item,
              approvals: item.approvals ?? [],
            };
          });
        }),
        take(1)
      );
  }

  // Helper funtions
  getService<T>(service: ProviderToken<T>): T {
    return this.injector.get<T>(service);
  }

  stringifyBooleans(arrayOfObjects: TargetMarkets[]): Array<any> {
    return arrayOfObjects.map((obj) => {
      for (const prop in obj) {
        if (obj[prop] === true) obj[prop] = 'True';
        if (obj[prop] === false) obj[prop] = 'False';
        else obj[prop];
      }
      return obj;
    });
  }

  stringToBoolConversion(string) {
    if (string == undefined || string === '') {
      return string;
    }
    return string.toLowerCase().includes('true') ? true : false;
  }

  getApprovalsTeams(): string[] {
    const key = this.activeTableKey as STATIC_DATA_TYPE;
    return Object.entries(APPROVALS_TEAMS_PER_STATIC_DATA_TYPE)
      .filter(([_, sdTypes]) => sdTypes.includes(key))
      .map(([team, _]) => team);
  }

  private getTablePermissions(): Observable<
    { [key in 'VIEW' | 'ADD_NEW' | 'DELETE' | 'EDIT']: string }
  > {
    return this.activeTableKey$.pipe(
      map((tableKey) => {
        return {
          VIEW: this.buildStaticDataPermission('VIEW'),
          ADD_NEW: this.buildStaticDataPermission('ADD_NEW'),
          DELETE: this.buildStaticDataPermission('DELETE'),
          EDIT: this.buildStaticDataPermission('EDIT'),
        };
      })
    );
  }

  buildStaticDataPermission(
    action: 'VIEW' | 'ADD_NEW' | 'DELETE' | 'EDIT',
    key: ActiveTableKeyType | '' = this.activeTableKey
  ): string {
    return `${STATIC_DATA_PERMISSIONS[key]}_${action}_STATIC_DATA${
      action !== 'VIEW' ? '_ITEMS' : ''
    }`;
  }
}
